import React, { useState, useEffect, useMemo } from 'react';
import ModelsAPI from '../../api/models';
import {
  AUDIO_PROVIDERS,
  CHAT_PROVIDERS,
  IMAGE_PROVIDERS,
  ProviderDefinition,
  Provider,
  VideoProvider,
  VIDEO_PROVIDERS,
  providerRequiresModel,
} from '../../api/structs/providers';
import { AIModel, formatPrice, formatCreditPrice } from '../../api/structs/model';
import { parseVideoCapabilities } from '../../api/videoCapabilities';
import { CheckpointAssetContext, readProfileCompatibility } from '../pipeline/validatePipeline';
import { useCostDisplay } from '../../hooks/useCostDisplay';
import { useOfferedProviders } from '../../hooks/useOfferedProviders';
import { Select, ModelPickerSheet } from '../ui';


type SelectorModality = 'chat' | 'image' | 'video' | 'audio';

/**
 * Narrows the catalog to the models a use can actually run on, beyond "this
 * provider serves it" — e.g. an agent needs tool calling.
 */
export interface ModelGate {
  /** '' when the model qualifies, else the warning for it (shown when it is
   *  the selected model). Given the whole catalog so the warning can point
   *  at a usable alternative. */
  check: (model: AIModel, catalog: AIModel[]) => string;
  /** What qualifying means, for "No <provider> <modality> models <purpose>." */
  purpose: string;
  /** The chip on a selected model that fails the gate. */
  badge: string;
}

interface CheckpointProviderSelectorProps {
  provider: string;
  model: string;
  modality?: SelectorModality;
  onProviderChange: (provider: string) => void;
  onModelChange: (model: string) => void;
  requireSeedImageSupport?: boolean;
  allowManualModelInput?: boolean;
  providerAriaLabel?: string;
  modelAriaLabel?: string;
  onValidationChange?: (message: string) => void;
  onSelectedPrice?: (price: string) => void;
  /** Overrides for the empty-state placeholder text, e.g. to surface a
   *  pipeline-level default a blank field will fall back to on save. */
  providerPlaceholder?: string;
  modelPlaceholder?: string;
  /** Chat providers offered beyond CHAT_PROVIDERS once the catalog lists a
   *  model of theirs (EXTRA_CHAT_PROVIDERS, for a pipeline checkpoint's model).
   *  Pass a module constant: the list is an effect dependency. */
  extraProviders?: ProviderDefinition[];
  /**
   * What the checkpoint being edited can supply to its generator at run time —
   * build it with `readCheckpointAssetContext` (validatePipeline.ts), so the
   * picker and the validator judge a checkpoint by the same predicates. Given
   * it, the picker flags generator profiles this checkpoint cannot feed —
   * img2img/inpaint profiles need a seed image, inpaint also a "mask"
   * attachment. Omit it (non-pipeline call sites) and no profile is flagged.
   */
  checkpointAssetContext?: CheckpointAssetContext;
  /**
   * Lists only the models that pass the gate. A model already selected that
   * fails it stays listed, chipped and explained with the gate's warning —
   * never hidden and never silently replaced, so opening a panel cannot
   * rewrite saved config.
   */
  modelGate?: ModelGate;
}

const PROVIDER_OPTIONS_BY_MODALITY: Record<SelectorModality, ProviderDefinition[]> = {
  chat: CHAT_PROVIDERS,
  image: IMAGE_PROVIDERS,
  video: VIDEO_PROVIDERS,
  audio: AUDIO_PROVIDERS,
};

const supportsReferenceInput = (constraints: Record<string, unknown> | undefined): boolean =>
  Boolean(
    constraints &&
      (constraints.supports_seed_image === true || constraints.supports_reference_images === true)
  );

/** Same "can this model take a reference-image input" question, for VIDEO —
 *  read through the typed parser rather than the image modality's raw keys. */
const supportsVideoReferenceInput = (constraints: Record<string, unknown> | undefined): boolean =>
  parseVideoCapabilities(constraints ?? {}).referenceImages > 0;

/**
 * The caveat to show for a generator profile the edited checkpoint cannot fully
 * feed, or '' when it can (or when nothing is known). Incompatible profiles stay
 * listed and selectable on purpose: selecting one surfaces the same finding
 * through the normal validation UI (canvas badges / problems drawer), which
 * explains the gap far better than a silently missing entry would.
 */
const profileCompatibilityGaps = (
  model: AIModel,
  context: CheckpointAssetContext | undefined
): string[] => {
  if (!context || model.provider !== 'profile') return [];
  const compatibility = readProfileCompatibility(model);
  // No metadata (a registry predating the fields) means no chip. The validator
  // is deliberately louder here — it emits a "couldn't be verified" warning —
  // because one warning in the problems drawer informs, while an "unknown"
  // chip on every row of the picker is noise the user cannot act on.
  if (!compatibility) return [];
  const missing: string[] = [];
  if (compatibility.requiresSeedImage && !context.hasImageSource) missing.push('seed image');
  if (compatibility.requiresMask && !context.hasMaskSource) missing.push('mask');
  return missing;
};

/** The chip text for {@link profileCompatibilityGaps}, or '' when nothing is missing. */
const profileCompatibilityBadge = (
  model: AIModel,
  context: CheckpointAssetContext | undefined
): string => {
  const missing = profileCompatibilityGaps(model, context);
  return missing.length > 0 ? `needs ${missing.join(' + ')}` : '';
};

const CheckpointProviderSelector: React.FC<CheckpointProviderSelectorProps> = ({
  provider,
  model,
  modality = 'chat',
  onProviderChange,
  onModelChange,
  requireSeedImageSupport = false,
  allowManualModelInput = false,
  providerAriaLabel,
  modelAriaLabel,
  onValidationChange,
  onSelectedPrice,
  providerPlaceholder,
  modelPlaceholder,
  extraProviders,
  checkpointAssetContext,
  modelGate,
}) => {
  const display = useCostDisplay();
  // Ties the warning below to the model picker it is about.
  const messageId = React.useId();
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [providerValidationMessage, setProviderValidationMessage] = useState('');
  // Which (modality, provider) pair the current `models` were fetched for.
  // "The catalog answered for what is selected right now", as distinct from
  // "we have no models": a failed fetch, an in-flight one, or a list still
  // belonging to the provider we just switched away from must never drive the
  // selection. Derived during render, so there is no effect-ordering window
  // where a stale success flag is read alongside a fresh provider.
  const [loadedCatalogKey, setLoadedCatalogKey] = useState<string | null>(null);
  const catalogKey = `${modality}:${provider}`;
  const catalogLoaded = loadedCatalogKey === catalogKey;

  const offeredExtra = useOfferedProviders(modality === 'chat' ? extraProviders : undefined, provider);
  const providerOptionsForModality = useMemo(
    () => (offeredExtra.length > 0 ? [...PROVIDER_OPTIONS_BY_MODALITY[modality], ...offeredExtra] : PROVIDER_OPTIONS_BY_MODALITY[modality]),
    [modality, offeredExtra]
  );
  const knownProviders = useMemo(
    () => new Set(providerOptionsForModality.map((item) => item.value)),
    [providerOptionsForModality]
  );

  const providerOptions = provider && !knownProviders.has(provider)
    ? [...providerOptionsForModality, { value: provider, label: `${provider} (Unsupported)` }]
    : providerOptionsForModality;

  useEffect(() => {
    if (!provider) {
      setModels([]);
      setProviderValidationMessage('');
      return;
    }

    if (!knownProviders.has(provider)) {
      setModels([]);
      setProviderValidationMessage(
        `Provider "${provider}" is not available for this checkpoint type. Choose a listed provider.`
      );
      return;
    }

    if (!providerRequiresModel(provider as Provider)) {
      setModels([]);
      setProviderValidationMessage('');
      return;
    }

    // Guards every state write below: a provider/modality switch abandons the
    // in-flight request, so a slow response can never repopulate (or blank) the
    // list that belongs to the newly selected provider.
    let cancelled = false;

    const fetchModels = async () => {
      setLoading(true);
      try {
        let fetched: AIModel[] = [];
        if (modality === 'chat') {
          fetched = await ModelsAPI.getChatModels();
        } else if (modality === 'image') {
          fetched = await ModelsAPI.getImageModels(provider);
        } else if (modality === 'video') {
          fetched = await ModelsAPI.getVideoModels(provider as VideoProvider);
        } else if (modality === 'audio') {
          fetched = await ModelsAPI.getAudioModels(provider);
        }

        if ((modality === 'image' || modality === 'video') && requireSeedImageSupport) {
          const checkCompatible = modality === 'video' ? supportsVideoReferenceInput : supportsReferenceInput;
          const compatibility = await Promise.all(
            fetched.map(async (candidate) => {
              try {
                const constraints = await ModelsAPI.getModelConstraints(candidate.id, modality);
                return { model: candidate, compatible: checkCompatible(constraints.capabilities) };
              } catch (error) {
                // Unknown ≠ incompatible: a registry hiccup must not silently
                // empty the picker. Keep the model — the backend re-validates
                // seed/reference compatibility when the pipeline is saved.
                console.error(`Failed to load constraints for ${candidate.id}:`, error);
                return { model: candidate, compatible: true };
              }
            })
          );
          if (cancelled) return;
          const compatibleModels = compatibility
            .filter((entry) => entry.compatible)
            .map((entry) => entry.model);
          setModels(compatibleModels);
          setLoadedCatalogKey(`${modality}:${provider}`);
          setProviderValidationMessage(
            compatibleModels.length === 0
              ? `No ${provider} ${modality} models with seed/reference support are available. Choose another provider.`
              : ''
          );
        } else {
          if (cancelled) return;
          setModels(fetched);
          setLoadedCatalogKey(`${modality}:${provider}`);
          setProviderValidationMessage('');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch checkpoint models:', error);
        setModels([]);
        setLoadedCatalogKey(null);
        setProviderValidationMessage('Failed to fetch models from registry.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchModels();

    return () => {
      cancelled = true;
    };
  }, [provider, modality, knownProviders, requireSeedImageSupport]);

  // Image/video/audio catalogs arrive already scoped to the provider, but the
  // chat catalog is one flat cross-provider list (`getChatModels()` unions
  // openrouter + vertexai), so the narrowing has to happen here — otherwise
  // picking Vertex AI still lists every OpenRouter model and a mismatched pair
  // becomes savable. Applying it to every modality also keeps the picker honest
  // if any provider-scoped endpoint ever hands back a mixed bucket. A model
  // with no `provider` field is kept either way (registry-lean/older entries
  // shouldn't vanish), and there is deliberately no "show everything instead"
  // fallback: an empty picker is the honest signal.
  const providerModels = useMemo(
    () => models.filter((m) => !m.provider || m.provider === provider),
    [models, provider]
  );
  // What may be picked: the provider's models that pass the gate, if any.
  const visibleModels = useMemo(
    () => (modelGate ? providerModels.filter((m) => modelGate.check(m, models) === '') : providerModels),
    [providerModels, modelGate, models]
  );
  // The selected model's own entry when it is the provider's but fails the
  // gate: kept and flagged (below), with the gate's reason.
  const selectedGateFailure = (() => {
    if (!modelGate || !model) return '';
    const selected = providerModels.find((candidate) => candidate.id === model);
    return selected ? modelGate.check(selected, models) : '';
  })();

  useEffect(() => {
    if (!provider || !knownProviders.has(provider) || !providerRequiresModel(provider as Provider)) {
      return;
    }
    // Catalog not (yet) answered for this provider, or answered completely
    // empty — indistinguishable from an outage. Leave the persisted choice be.
    if (!catalogLoaded || models.length === 0) {
      return;
    }
    // The provider's own model stays even when it fails the gate: flagged
    // below with its reason, never silently swapped for another.
    if (model && providerModels.some((candidate) => candidate.id === model)) {
      return;
    }
    // A model the catalog has never heard of is not a cross-provider mismatch;
    // it keeps its "(Unsupported)" flag rather than being silently rewritten,
    // so opening a panel can never overwrite saved checkpoint config.
    if (model && !models.some((candidate) => candidate.id === model)) {
      return;
    }

    // Empty selection, or one belonging to the provider we just switched away
    // from. Land on something this provider can actually serve — and clear it
    // outright when it can serve nothing, so no mismatched pair is savable.
    const next = visibleModels[0]?.id ?? '';
    if (next !== model) {
      onModelChange(next);
    }
  }, [provider, model, models, providerModels, visibleModels, catalogLoaded, onModelChange, knownProviders]);

  const shouldUseManualModelInput =
    allowManualModelInput &&
    Boolean(provider) &&
    knownProviders.has(provider) &&
    !providerRequiresModel(provider as Provider);

  const modelIsUnsupported =
    Boolean(provider) &&
    knownProviders.has(provider) &&
    providerRequiresModel(provider as Provider) &&
    Boolean(model) &&
    !loading &&
    // Only a catalog that actually answered for this provider, with something
    // in it, may call a model unsupported — an outage or an in-flight fetch
    // must not masquerade as one.
    catalogLoaded &&
    models.length > 0 &&
    !providerModels.some((candidate) => candidate.id === model);

  // Same "catalog actually answered" guard as modelIsUnsupported, but for the
  // case where it answered non-empty and this provider simply isn't in it
  // (e.g. the chat catalog has openrouter models but none for vertexai). The
  // model-selection effect below silently clears `model` to '' here — without
  // this, that clear is invisible: the picker just shows "Select model" with
  // no explanation.
  const noModelsForSelectedProvider =
    Boolean(provider) &&
    knownProviders.has(provider) &&
    providerRequiresModel(provider as Provider) &&
    catalogLoaded &&
    models.length > 0 &&
    providerModels.length === 0;

  // The provider serves models, but none passes the gate.
  const noModelsPassTheGate = Boolean(modelGate) && catalogLoaded && providerModels.length > 0 && visibleModels.length === 0;

  const providerLabel =
    providerOptionsForModality.find((item) => item.value === provider)?.label ?? provider;

  // Checked ahead of modelIsUnsupported: when the provider serves nothing at
  // all, "no models are available" is the accurate message — "model X is
  // unavailable" implies other options exist to pick from, and here none do.
  // (modelIsUnsupported's own value is untouched — it still drives the
  // "(Unsupported)" dropdown entry below.)
  const validationMessage = noModelsForSelectedProvider
    ? `No ${providerLabel} ${modality} models are available. Choose another provider.`
    : modelIsUnsupported
    ? `Model "${model}" is unavailable for provider "${provider}". Select a listed model.`
    : selectedGateFailure
    ? selectedGateFailure
    : noModelsPassTheGate && modelGate
    ? `No ${providerLabel} ${modality} models ${modelGate.purpose}. Choose another provider.`
    : providerValidationMessage;

  useEffect(() => {
    onValidationChange?.(validationMessage);
  }, [onValidationChange, validationMessage]);

  useEffect(() => {
    if (!onSelectedPrice) return;
    const selectedModel = providerModels.find((m) => m.id === model);
    const fmt = display === 'credits' ? formatCreditPrice : formatPrice;
    onSelectedPrice(selectedModel ? fmt(selectedModel) : '');
  }, [model, providerModels, onSelectedPrice, display]);

  const fmt = display === 'credits' ? formatCreditPrice : formatPrice;
  const toOption = (m: AIModel) => {
    const isProfile = m.provider === 'profile';
    const mode = isProfile ? readProfileCompatibility(m)?.generationMode : undefined;
    return {
      value: m.id,
      // Prefix a visible "Profile" badge so published generator profiles are
      // distinguishable from upstream provider models in the dropdown.
      label: isProfile ? `[Profile] ${m.name}` : m.name,
      sublabel: isProfile ? `profile · ${mode ? `${mode} · ` : ''}${m.id}` : m.id,
      rightLabel: fmt(m),
      badge: profileCompatibilityBadge(m, checkpointAssetContext),
    };
  };
  const gateFailingSelection = selectedGateFailure
    ? providerModels.find((candidate) => candidate.id === model)
    : undefined;
  const dropdownOptions = [
    ...(gateFailingSelection ? [{ ...toOption(gateFailingSelection), badge: modelGate?.badge ?? '' }] : []),
    ...visibleModels.map(toOption),
  ];
  // Explains the gap for the model that is actually selected, right where the
  // user just picked it — the canvas badge / problems drawer says the same thing
  // once the template is validated.
  const selectedProfileGap = (() => {
    const selected = models.find((candidate) => candidate.id === model);
    if (!selected) return '';
    const missing = profileCompatibilityGaps(selected, checkpointAssetContext);
    if (missing.length === 0) return '';
    const mode = readProfileCompatibility(selected)?.generationMode;
    return `This profile runs in "${mode}" mode and needs ${missing
      .map((item) => `a ${item}`)
      .join(' and ')}, which this checkpoint does not declare.`;
  })();

  const optionsWithUnsupported = modelIsUnsupported
    ? [
        {
          value: model,
          label: `${model} (Unsupported)`,
          sublabel: model,
          rightLabel: '',
        },
        ...dropdownOptions,
      ]
    : dropdownOptions;

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-1 gap-2 min-w-0 sm:grid-cols-[minmax(0,9rem)_minmax(0,1fr)]">
        <Select
          options={providerOptions}
          value={provider}
          aria-label={providerAriaLabel}
          onChange={(e) => {
            onProviderChange(e.target.value);
          }}
          placeholder={providerPlaceholder || 'Select provider...'}
          selectSize="sm"
          className="w-full min-w-0"
        />

        {provider && knownProviders.has(provider) && providerRequiresModel(provider as Provider) && (
          <ModelPickerSheet
            options={optionsWithUnsupported}
            value={model}
            onChange={onModelChange}
            placeholder={modelPlaceholder || 'Select model'}
            searchable
            loading={loading}
            chipClassName="w-full min-w-0"
            ariaLabel={modelAriaLabel}
            ariaDescribedBy={validationMessage ? messageId : undefined}
          />
        )}

        {shouldUseManualModelInput && (
          <input
            type="text"
            value={model}
            onChange={(event) => onModelChange(event.target.value)}
            aria-label={modelAriaLabel}
            aria-describedby={validationMessage ? messageId : undefined}
            className="input input-sm w-full min-w-0"
            placeholder={modelPlaceholder || 'Enter model id'}
          />
        )}
      </div>

      {validationMessage && (
        <p id={messageId} className="text-[11px] text-amber-300">{validationMessage}</p>
      )}

      {selectedProfileGap && (
        <p className="text-[11px] text-amber-300">
          {selectedProfileGap} Add a required asset below (or an
          {' '}<code>attachments:&lt;checkpoint_id&gt;</code> input mapping) so this checkpoint can supply it.
        </p>
      )}
    </div>
  );
};

export default CheckpointProviderSelector;
